import React from 'react';
import { AlertCircle } from 'lucide-react';

const avatarColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#F97316'];

interface ChildForm {
  name: string;
  age: number;
  avatar_color: string;
}

interface ChildFormModalProps {
  isOpen: boolean;
  mode: 'add' | 'edit';
  formData: ChildForm;
  error: string | null;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
  onChange: (field: keyof ChildForm, value: string | number) => void;
  onUpgradeClick?: () => void;
}

export function ChildFormModal({
  isOpen,
  mode,
  formData,
  error,
  onSubmit,
  onClose,
  onChange,
  onUpgradeClick
}: ChildFormModalProps) {
  if (!isOpen) return null;

  const title = mode === 'add' ? 'Add New Child' : 'Edit Child Profile';
  const submitText = mode === 'add' ? 'Add Child' : 'Save Changes';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
        <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center font-baloo">{title}</h3>

        <form onSubmit={onSubmit} className="space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </div>
              {error.includes('upgrade') && onUpgradeClick && (
                <button
                  type="button"
                  onClick={onUpgradeClick}
                  className="mt-2 text-red-600 hover:text-red-500 font-medium underline"
                >
                  Upgrade Plan →
                </button>
              )}
            </div>
          )}

          {/* Name Input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Child's Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => onChange('name', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg text-gray-900 placeholder-gray-500"
              placeholder="Enter child's name"
              required
            />
          </div>

          {/* Age Select */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Age</label>
            <select
              value={formData.age}
              onChange={(e) => onChange('age', parseInt(e.target.value))}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg text-gray-900"
            >
              {[...Array(16)].map((_, i) => (
                <option key={i} value={i + 3}>{i + 3} years old</option>
              ))}
            </select>
          </div>

          {/* Avatar Color Picker */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">Avatar Color</label>
            <div className="flex space-x-3">
              {avatarColors.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => onChange('avatar_color', color)}
                  className={`w-10 h-10 rounded-full border-4 transition-all ${
                    formData.avatar_color === color ? 'border-gray-800 scale-110' : 'border-gray-300'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 font-semibold transition-colors font-baloo"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-600 text-white rounded-xl hover:opacity-90 font-semibold transition-opacity font-baloo"
            >
              {submitText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
